import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const BookingStatus = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingId) {
      fetchBookingStatus();
    }
  }, [bookingId]);

  const fetchBookingStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/test-bookings/${bookingId}`);
      if (response.data.success) {
        setBooking(response.data.booking);
        setQrCode(response.data.qrCode);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const downloadTicket = () => {
    if (qrCode) {
      const link = document.createElement('a');
      link.download = `ticket-${bookingId}.png`;
      link.href = qrCode;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-4">🎫</div>
          <div className="text-lg">Loading booking details...</div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <div className="text-xl font-bold text-gray-900 mb-2">Booking Not Found</div>
          <div className="text-gray-600">The booking ID you're looking for doesn't exist.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="text-center">
              <div className="text-4xl mb-2">🎫</div>
              <h1 className="text-2xl font-bold">Booking Confirmation</h1>
              <p className="text-blue-100">Booking ID: {booking._id}</p>
            </div>
          </div>

          {/* Status */}
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Booking Status</h3>
                <p className="text-gray-600">Payment Status: {booking.paymentStatus}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                {booking.status.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Booking Details */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Park Name</p>
                <p className="font-semibold">{booking.ticketTypeId?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-semibold">{booking.ticketTypeId?.city}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Quantity</p>
                <p className="font-semibold">{booking.quantity} tickets</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="font-semibold text-green-600">₹{booking.totalAmount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Visit Date</p>
                <p className="font-semibold">{new Date(booking.visitDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Booking Date</p>
                <p className="font-semibold">{new Date(booking.bookingDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-semibold">{booking.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{booking.customerEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{booking.customerPhone}</p>
              </div>
            </div>
          </div>

          {/* QR Code */}
          {qrCode && (
            <div className="p-6 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Ticket</h3>
              <div className="inline-block p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                <img src={qrCode} alt="Ticket QR Code" className="w-48 h-48 mx-auto" />
                <p className="text-sm text-gray-600 mt-2">Show this QR code at the entrance</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={downloadTicket}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Download Ticket
                </button>
              </div>
            </div>
          )}

          {/* Payment Pending */}
          {booking.paymentStatus === 'pending' && (
            <div className="p-6 bg-yellow-50 text-center">
              <div className="text-4xl mb-2">⏳</div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Payment Pending</h3>
              <p className="text-yellow-700">Please complete your payment to confirm the booking.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingStatus;